import { SortingState } from '@tanstack/react-table'
import { FilterWorkersWithVisibleItems } from 'components/buttons/FilterWorkersWithVisibleItemsButtonGroup'
import {
  useLocalStorageState,
  SchemaMigrations,
} from 'hooks/use-local-storage-state'
import { createSetOperationsFor } from '@utils/createSetOperations'
import { createSortingOperationsFor } from '@utils/createSortingOperations'
import { useMemo } from 'react'
import { Status } from 'types'
import { Anomalies } from 'components/buttons/AnomalousTimeCardButtons'

interface ApprovalsLocalStorageState {
  hiddenDays: Set<number>
  hiddenStatuses: Set<Status>
  filteredAnomalyTypes: Set<Anomalies>
  filteredFacilityIds: Set<number>
  filteredApprovalDomainIds: Set<string>
  filteredDepartmentIds: Set<string>
  filterEmployeesWithVisibleApprovals: FilterWorkersWithVisibleItems
  sorting: SortingState
  employeeFilter: {
    name: string
    // TODO - determine if salaried/hourly should be included
    // type: ('salaried' | 'hourly' | 'unmanaged' | 'adminManaged')[]
    type: ('unmanaged' | 'adminManaged')[]
  }
}

const defaultValues: ApprovalsLocalStorageState = {
  hiddenDays: new Set(),
  hiddenStatuses: new Set(),
  filteredAnomalyTypes: new Set(),
  filteredFacilityIds: new Set(),
  filteredApprovalDomainIds: new Set(),
  filteredDepartmentIds: new Set(),
  filterEmployeesWithVisibleApprovals: 'all',
  sorting: [
    {
      id: 'employeeName',
      desc: false,
    },
  ],
  // An empty `type` array means "all types selected" (see EmployeeFilter and
  // useFiltering). Defaulting to [] avoids having to enumerate every type here
  // and keeps new types opted-in automatically.
  employeeFilter: { name: '', type: [] },
}

const SCHEMA_VERSION = 1

export const migrations: SchemaMigrations = {
  // v1: the flat `employeeNameFilter: string` became
  // `employeeFilter: { name, type }`. Carry the user's typed name across rather
  // than dropping it on the floor.
  //
  // Version 0 is ambiguous: an un-versioned blob can be either the truly-old
  // `employeeNameFilter` shape OR the new `employeeFilter` shape written by the
  // release that shipped the rename but predates the version envelope. So this
  // must be idempotent — only synthesize `employeeFilter` from the legacy key,
  // and leave an already-present `employeeFilter` (name + type selections) alone.
  1: ({ employeeNameFilter, employeeFilter, ...rest }) => ({
    ...rest,
    employeeFilter: employeeFilter ?? {
      name: typeof employeeNameFilter === 'string' ? employeeNameFilter : '',
      type: [],
    },
  }),
}

export const useApprovalsLocalStorage = () => {
  const { state, setState, debounced, clear, isChanged } = useLocalStorageState(
    'timeLoggerApprovalsState',
    defaultValues,
    { version: SCHEMA_VERSION, migrations },
  )

  return useMemo(
    () => ({
      hiddenDays: createSetOperationsFor('hiddenDays')(state, setState),
      hiddenStatuses: createSetOperationsFor('hiddenStatuses')(state, setState),
      filteredAnomalyTypes: createSetOperationsFor('filteredAnomalyTypes')(
        state,
        setState,
      ),
      filteredFacilityIds: {
        ...createSetOperationsFor('filteredFacilityIds')(state, setState),
        debounced: debounced?.filteredFacilityIds ?? new Set(),
      },
      filteredApprovalDomainIds: {
        ...createSetOperationsFor('filteredApprovalDomainIds')(state, setState),
        debounced: debounced?.filteredApprovalDomainIds ?? new Set(),
      },
      filteredDepartmentIds: {
        ...createSetOperationsFor('filteredDepartmentIds')(state, setState),
        debounced: debounced?.filteredDepartmentIds ?? new Set(),
      },
      filterEmployeesWithVisibleApprovals: {
        value: state.filterEmployeesWithVisibleApprovals,
        debounced: debounced?.filterEmployeesWithVisibleApprovals ?? '',
        set: (value: FilterWorkersWithVisibleItems) => {
          setState((prev) => ({
            ...prev,
            filterEmployeesWithVisibleApprovals: value,
          }))
        },
      },
      sorting: createSortingOperationsFor('sorting')(state, setState),
      employeeFilter: {
        value: state.employeeFilter,
        debounced: debounced?.employeeFilter ?? state.employeeFilter,
        set: (value: ApprovalsLocalStorageState['employeeFilter']) => {
          setState((prev) => ({
            ...prev,
            employeeFilter: value,
          }))
        },
      },
      clear,
      isChanged,
    }),
    [state, setState, debounced, clear, isChanged],
  )
}
